import './layout.less';
import React, { useEffect, useRef } from 'react';
import { Editor } from '@meitu/saas-editor-core';
import { type ContextMenuPlugin } from '@meitu/saas-editor-core/es/plugins/context-menu';
import { EditorContext } from '@/hooks/use-editor';
import { useLocaleStore } from '@/i18n/index';
import { useViewportHeightVar } from '@/layout/hooks';
import { getLayoutNode, getRenderNode, OptionType } from '../type';
import Collapse from './collapse';
import ColsContainer from './cols';
import Header from './header';
import Menu from './menu';
import Panel from './panel';
import Stage from './stage';

export const LayoutOption: React.FC<{
    type: OptionType;
    children?: React.ReactNode;
    theme?: string;
    currentCollapse?: string;
    isShowCollapse?: boolean;
    isShowAsideBtn?: boolean;
    isShowPanel?: boolean;
    stageBottomChildren?: React.ReactNode;
    onClick?: () => void;
    onResize?: (width: number, height: number) => void;
}> = React.memo(
    ({
        type,
        currentCollapse = '',
        isShowPanel,
        onResize,
        children,
        stageBottomChildren,
        isShowCollapse,
        isShowAsideBtn,
        onClick,
    }) => {
        const nodes: React.ReactNode[] = [];
        if ((type === OptionType.cols1 || type === OptionType.cols2 || type === OptionType.cols3) && children) {
            React.Children.forEach(children, (child) => {
                if (child !== null) {
                    nodes.push(child);
                }
            });
        }

        switch (type) {
            case OptionType.header:
                return <Header key={1}>{children}</Header>;
            case OptionType.menu:
                return <Menu key={2}>{children}</Menu>;
            case OptionType.collapse:
                return (
                    <Collapse isShowCollapse={isShowCollapse} key={3} currentCollapse={currentCollapse}>
                        {children}
                    </Collapse>
                );
            case OptionType.panel:
                return (
                    <Panel isShowPanel={isShowPanel} key={4}>
                        {children}
                    </Panel>
                );
            case OptionType.stage: {
                return (
                    <div className="p-editor__stage__combination">
                        <Stage
                            isShowCollapse={isShowCollapse}
                            isShowAsideBtn={isShowAsideBtn}
                            onResize={onResize}
                            onClick={onClick}
                            key={5}
                        >
                            {children}
                        </Stage>
                        {stageBottomChildren}
                    </div>
                );
            }
            // TODO: @cxz1 临时方案
            case OptionType.photoEditorStage: {
                return (
                    <div className="p-editor__photo__editor">
                        <Stage
                            isShowCollapse={isShowCollapse}
                            isShowAsideBtn={isShowAsideBtn}
                            onResize={onResize}
                            onClick={onClick}
                            key={5}
                        >
                            {children}
                        </Stage>
                        {stageBottomChildren}
                    </div>
                );
            }
            case OptionType.cols2:
                return nodes.length ? (
                    <ColsContainer onResize={onResize} cols={2} key={6}>
                        {nodes}
                    </ColsContainer>
                ) : null;
            case OptionType.cols3:
                return nodes.length ? (
                    <ColsContainer onResize={onResize} cols={3} key={7}>
                        {nodes}
                    </ColsContainer>
                ) : null;
            case OptionType.cols1:
                return nodes.length ? (
                    <ColsContainer onResize={onResize} cols={1} key={8}>
                        {nodes}
                    </ColsContainer>
                ) : null;
        }
        return <>{children}</>;
    },
);

const Layout: React.FC<{
    editor: Editor;
    children?: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}> = ({ editor, children, className = '', style }) => {
    const { locale } = useLocaleStore();
    const dragDefault = (ev: any) => {
        ev.preventDefault();
    };
    getLayoutNode(children as any[]);
    const stage = getRenderNode(OptionType.stage);
    const photoEditorStage = getRenderNode(OptionType.photoEditorStage);
    const panel = getRenderNode(OptionType.panel);
    const header = getRenderNode(OptionType.header);
    const menu = getRenderNode(OptionType.menu);
    const collapse = getRenderNode(OptionType.collapse);
    const cols2 = getRenderNode(OptionType.cols2);
    const cols3 = getRenderNode(OptionType.cols3);
    const extra = getRenderNode(OptionType.extra);

    useViewportHeightVar();

    const asideRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        return editor.getInstance<ContextMenuPlugin>('ContextMenuPlugin')?.onState((isShow) => {
            if (asideRef.current) {
                asideRef.current.style.zIndex = isShow ? '1' : '10';
            }
        });
    }, []);

    return (
        <EditorContext.Provider value={editor}>
            <div
                className={`p-editor ${className}`}
                onDragOver={dragDefault}
                onDrop={dragDefault}
                style={
                    {
                        '--header-height': '54px',
                        '--menu-width': locale === 'en' ? '85px' : '66px',
                        ...style,
                    } as React.CSSProperties
                }
            >
                {extra}
                {header}
                <div className="p-editor__container">
                    <div ref={asideRef} className="p-editor__aside">
                        {menu}
                        <div className="p-editor__collapse-container">
                            {collapse}
                            {cols3}
                        </div>
                    </div>
                    <div className="p-editor__main">
                        {photoEditorStage}
                        {stage}
                        {panel}
                        {cols2}
                    </div>
                </div>
            </div>
        </EditorContext.Provider>
    );
};

export default Layout;
